import { useMutation } from 'react-query';

import { putV2ApiCompanyIdAccountingIntegrationsSyncSettingsFieldId } from 'src/client/spendesk-api';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';

import { useInvalidateSyncOperationQuery } from './useGetSyncOperation';
import { useInvalidateSyncSettingsQuery } from './useGetSyncSettings';

const syncModeOptions = [
  'pull',
  'pullAndMap',
  'none',
  'push',
  'bidirectional',
] as const;

export type SyncMode = (typeof syncModeOptions)[number];
export const isSyncMode = (input: string | null): input is SyncMode =>
  // eslint-disable-next-line unicorn/prefer-array-some
  !!syncModeOptions.find((value) => value === input);

type UpdateSyncSettings = {
  fieldId: string;
  syncMode: SyncMode;
};

type UpdateSyncSettingsOptions = {
  onSuccess: () => void;
  onError?: () => void;
};

export const useUpdateSyncSettings = (input?: UpdateSyncSettingsOptions) => {
  const companyId = useCompanyId();
  const invalidateSyncSettingsQuery = useInvalidateSyncSettingsQuery();
  const invalidateSyncOperationQuery = useInvalidateSyncOperationQuery();
  const { mutate, isLoading } = useMutation({
    mutationKey: ['syncSettings', companyId],
    mutationFn: async ({ fieldId, syncMode }: UpdateSyncSettings) => {
      const response =
        await putV2ApiCompanyIdAccountingIntegrationsSyncSettingsFieldId({
          path: {
            fieldId,
            companyId,
          },
          body: {
            syncMode,
          },
        });

      if (response.statusCode === 200) {
        return response.body;
      }

      throw new Error('Failed to update sync settings');
    },
    onSuccess: () => {
      invalidateSyncSettingsQuery();
      invalidateSyncOperationQuery();
      input?.onSuccess();
    },
    onError: input?.onError,
  });

  return {
    isPending: isLoading,
    updateSyncSettings: mutate,
  };
};
